import { CSSProperties, FC, ReactNode, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Table, TableProps, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { checkIsSafari } from 'utils/extra.utils';
import type { RootState } from 'store';
import EditableRow from './EditableRow';
import EditableCell from './EditableCell';
import useColumns, { AppTableColumn } from './useColumns';
import styles from './appTable.module.less';
import type { AppTableInitialState, AppTableSlice } from './store';
import AlertSelectedRows from './AlertSelectedRows';
import AppTablePagination from './AppTablePagination';
import useUrlParamFilters from './useUrlParamFilters';

interface AppTableProps {
  storeSlice: AppTableSlice;
  columns: AppTableColumn[];
  className?: string;
  containerStyle?: CSSProperties;
  widthX?: string | number;
  widthY?: string | number;
  rowKey?: string;
  size?: SizeType;
  textCount?: ReactNode;
  prefixPagination?: string;
  childrenColumnName?: string;
  triggerSilenceReload?: number;
  resetSortOnMount?: { column: string; order: 'ascend' | 'descend' };
  editable?: boolean;
  fullPage?: boolean;
  hideActionColumn?: boolean;
  hideRowSelection?: boolean;
  hidePagination?: boolean;
  /** Custom empty-state content shown when there are no rows (defaults to antd's empty). */
  emptyText?: ReactNode;
  /** Fit columns to the container width (table-layout: fixed, no horizontal scroll). Lets percentage widths apply. */
  fitColumns?: boolean;
  /**
   * Render without a scroll viewport so the table sizes to its content (no inner vertical scroll
   * or expandable-row height quirks, no forced horizontal scroll). For short, content-sized tables.
   */
  autoHeight?: boolean;
  /** Expandable-row config passed straight to antd's Table (e.g. expandedRowRender). */
  expandable?: TableProps<any>['expandable'];
  rowClassName?: TableProps<any>['rowClassName'];
  onRowUpdate?: (uuid: string, values: Record<string, any>) => void;
  onRowClick?: (row: Record<string, any>, event: React.MouseEvent<HTMLElement>) => void;
  onDoubleClick?: (row: Record<string, any>, event: React.MouseEvent<HTMLElement>) => void;
  onRenderData?: (data: Record<string, any>[]) => Record<string, any>[];
  applyRenderDataBeforePagination?: boolean;
}

const AppTable: FC<AppTableProps> = ({
  storeSlice,
  columns: propColumns,
  className,
  containerStyle,
  widthX,
  widthY,
  rowKey = 'uuid',
  size = 'small',
  textCount,
  prefixPagination,
  childrenColumnName,
  triggerSilenceReload,
  resetSortOnMount,
  editable = false,
  fullPage = false,
  hideActionColumn = false,
  hideRowSelection = false,
  hidePagination = false,
  emptyText,
  fitColumns = false,
  autoHeight = false,
  expandable,
  rowClassName,
  onRowUpdate,
  onRowClick,
  onDoubleClick,
  onRenderData,
  applyRenderDataBeforePagination = false,
}) => {
  const store = useSelector((globalStore: RootState) => globalStore[storeSlice.name] as AppTableInitialState);
  const triggerSilenceReloadRef = useRef(triggerSilenceReload);
  const resetSortOnMountRef = useRef(resetSortOnMount);

  if (resetSortOnMount !== resetSortOnMountRef.current) {
    resetSortOnMountRef.current = resetSortOnMount;
  }

  useUrlParamFilters({ store, storeSlice });

  const dataSource = useMemo(() => {
    let newData = store.data;

    // Apply onRenderData before pagination if flag is set (e.g., for compacting data)
    if (applyRenderDataBeforePagination && onRenderData) {
      newData = onRenderData(newData);
    }

    // Apply pagination if needed
    if (!store.serverControl && store.pagination) {
      newData = newData.slice(
        store.pagination.pageSize * (store.pagination.current - 1),
        store.pagination.pageSize * store.pagination.current,
      );
    }

    // Apply onRenderData after pagination if flag is not set (original behavior)
    if (!applyRenderDataBeforePagination && onRenderData) {
      newData = onRenderData(newData);
    }

    return newData;
  }, [store.serverControl, store.pagination, store.data, onRenderData, applyRenderDataBeforePagination]);

  const dispatch = useDispatch();

  if (triggerSilenceReloadRef.current !== triggerSilenceReload) {
    triggerSilenceReloadRef.current = triggerSilenceReload;

    if (!store.loading) {
      dispatch(storeSlice.actions.fetch({ silenceLoading: true }));
    }
  }

  const columns = useColumns({
    columns: propColumns,
    sort: store.params.sort,
    editable,
    onSave: onRowUpdate,
  });

  const renderComponents: TableProps<any>['components'] = editable
    ? {
        body: {
          row: EditableRow,
          cell(props: any) {
            if (!props.editable) {
              return <td {...props} />;
            }

            const currentEl = columns.find((el) => el.dataIndex === props.dataIndex);

            return (
              <EditableCell
                {...props}
                editComponent={currentEl?.editComponent}
                beforeSave={currentEl?.beforeSave}
                onToggleEdit={currentEl?.onToggleEdit}
              />
            );
          },
        },
      }
    : undefined;

  let scrollX: string | number | boolean | undefined = 'max-content';
  if (fitColumns) {
    scrollX = undefined;
  } else if (!store.data?.length) {
    scrollX = widthX ?? true;
  }
  const defaultScrollY = fullPage ? 'calc(100% - 24px)' : window.innerHeight - 450;

  // `autoHeight` drops the scroll viewport entirely so the table grows with its content.
  const scroll: TableProps<any>['scroll'] = autoHeight
    ? undefined
    : { y: widthY ?? defaultScrollY, x: editable && checkIsSafari() ? widthX ?? true : scrollX };

  useEffect(() => {
    if (resetSortOnMountRef.current) {
      dispatch(storeSlice.actions.setSort({ ...resetSortOnMountRef.current }));
    } else {
      dispatch(storeSlice.actions.fetch());
    }
  }, [dispatch, storeSlice]);

  return (
    <div className={styles.tableContainer} style={containerStyle}>
      <AlertSelectedRows
        rowsTotal={store.pagination?.total ?? store.data.length}
        dataLength={store.data.length}
        selectedRowKeys={store.selectedRows}
        selectedAllPages={store.selectedAllPages}
        onToggleSelectedAllPages={() => dispatch(storeSlice.actions.toggleSelectedAllPages({ rowKey }))}
      />

      <Table
        columns={[
          ...columns,
          ...(onDoubleClick && !hideActionColumn
            ? [
                {
                  width: 40,
                  title: '',
                  dataIndex: 'action',
                  fixed: 'right' as const,
                  render: (_: any, row: any) => {
                    return (
                      <Tooltip title="View">
                        <Button size="small" type="link" icon={<EyeOutlined />} onClick={(event) => onDoubleClick(row, event)} />
                      </Tooltip>
                    );
                  },
                },
              ]
            : []),
        ]}
        dataSource={dataSource}
        loading={store.loading && !store.silenceLoading}
        size={size}
        className={className}
        rowClassName={rowClassName}
        scroll={scroll}
        tableLayout={fitColumns ? 'fixed' : undefined}
        components={renderComponents}
        pagination={false}
        showSorterTooltip={false}
        locale={emptyText ? { emptyText } : undefined}
        expandable={expandable}
        rowKey={rowKey}
        childrenColumnName={childrenColumnName}
        rowSelection={
          hideRowSelection
            ? undefined
            : {
                selectedRowKeys: store.selectedRows,
                onChange: (keys) => {
                  dispatch(storeSlice.actions.setSelectedRows(keys));
                },
                onSelectAll: (selected) => {
                  if (selected) {
                    dispatch(storeSlice.actions.setSelectedRows(store.data.map((el: any) => el[rowKey])));
                  } else {
                    dispatch(storeSlice.actions.setSelectedRows([]));
                  }
                },
              }
        }
        onChange={(_pagination, _filters, sort) => {
          if (Array.isArray(sort)) {
            return;
          }

          let field = Array.isArray(sort.field) ? sort.field.join('.') : sort.field;

          field = (sort.column as any)?.sorterKey ?? field;

          dispatch(
            storeSlice.actions.setSort(
              field && sort.order
                ? {
                    column: field.toString(),
                    order: sort.order,
                  }
                : null,
            ),
          );
        }}
        onRow={(row) => ({
          onClick: (event) => onRowClick?.(row, event),
          onDoubleClick: (event) => onDoubleClick?.(row, event),
        })}
      />

      <AppTablePagination
        storeSlice={storeSlice}
        size={size}
        textCount={textCount}
        prefixPagination={prefixPagination}
        hidePagination={hidePagination}
      />
    </div>
  );
};

export default AppTable;

export { styles as appTableStyles };
